import { Pipe, PipeTransform, Inject, LOCALE_ID } from '@angular/core';
import { formatNumber } from '@angular/common';
import { CurrencyService } from '../services/currency.service';

/**
 * Formats a KES (base) amount in the user's chosen display currency, e.g.
 * `132000 | corpCurrency` -> "USD 1,000". Conversion uses the same rates the
 * backend publishes at /currencies/ (sourced from Itinerary currency_rates_v2),
 * so display is consistent app-wide. Settlement stays KES.
 *
 * Usage:
 *   {{ amountKes | corpCurrency }}               -> "USD 1,000"
 *   {{ amountKes | corpCurrency:'1.2-2' }}       -> "USD 1,000.00"
 *   {{ amountKes | corpCurrency:'1.0-0':false }} -> "1,000"  (no currency code)
 *
 * Impure so it re-evaluates when rates finish loading or the user switches
 * currency; the transform itself is a cheap multiply + format.
 */
@Pipe({
  name: 'corpCurrency',
  standalone: true,
  pure: false,
})
export class CorpCurrencyPipe implements PipeTransform {
  constructor(
    private currencyService: CurrencyService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  transform(
    amountKes: number | string | null | undefined,
    digitsInfo: string = '1.0-0',
    showCode: boolean = true
  ): string {
    if (amountKes === null || amountKes === undefined || amountKes === '') {
      return '';
    }
    const value = typeof amountKes === 'string' ? Number(amountKes) : amountKes;
    if (value === null || value === undefined || isNaN(value as number)) {
      return '';
    }

    const d = this.currencyService.display(value as number);
    const formatted = d.amount === null ? '' : formatNumber(d.amount, this.locale, digitsInfo);
    return showCode ? `${d.currency} ${formatted}`.trim() : formatted;
  }
}
